import type { KeyboardEvent, MouseEvent, ChangeEvent } from 'react';
import { useState, useMemo, useCallback, useRef } from 'react';
import type {
    TChangeEventHandler,
    TFocusEventHandler,
    TEventHandler,
    TMouseEventHandler,
} from '@alfa-bank/site-input/models';

import type { TInputSelection, IUsePhoneInput } from './models';
import {
    closestEditableIndex,
    getFormatFunction,
    parseMask,
    setInputSelection,
    correctCarriagePosition,
} from './helpers';

export const usePhoneInput = ({
    initValue,
    inputRef,
    onCancel,
    onChange,
    onFocus,
    onKeyDown,
    type,
}: IUsePhoneInput) => {
    const maskOptions = useMemo(() => parseMask(type), [type]);
    const isDeleteOperation = useRef<boolean>(false);
    const getFormatValue = useMemo(() => getFormatFunction(maskOptions, type), [maskOptions, type]);
    const [value, setValue] = useState(getFormatValue(String(initValue)));

    const beforeInputSelection = useRef<TInputSelection>({
        start: maskOptions.prefix.length,
        end: maskOptions.prefix.length,
    });
    const handleChange: TChangeEventHandler = useCallback(
        ({ event, name, value: newValue }) => {
            const formattedValue = getFormatValue(String(newValue));

            setValue(formattedValue);

            if (initValue === '' || initValue === maskOptions.prefix) {
                beforeInputSelection.current = {
                    start: formattedValue.length,
                    end: formattedValue.length,
                };
            } else
                beforeInputSelection.current = {
                    start: inputRef.current?.selectionStart || null,
                    end: inputRef.current?.selectionEnd || null,
                };

            if (onChange) onChange({ value: formattedValue, name, event });
        },
        [getFormatValue, inputRef, onChange, initValue, maskOptions]
    );

    const handleKeyDown: TEventHandler = useCallback(
        ({ event, name, value: newValue }) => {
            const { permanents, prefix } = maskOptions;
            const { key, target } = event as KeyboardEvent<HTMLInputElement>;
            const { selectionStart } = target as HTMLInputElement;
            const carriagePositionOnPrefix = Number(selectionStart) <= prefix.length;

            // не пускать каретку левее скобочек "("
            if (key === 'ArrowLeft' && carriagePositionOnPrefix) {
                event.preventDefault();
            }

            if (
                key === 'Backspace' &&
                inputRef.current?.selectionStart === inputRef.current?.selectionEnd
            ) {
                isDeleteOperation.current = true;
                const permIndex = permanents.some(
                    idx => idx + 1 === inputRef.current?.selectionEnd
                );

                if (permIndex) {
                    const closestIdx = closestEditableIndex(
                        prefix.length,
                        Number(inputRef.current?.selectionEnd) - 1,
                        permanents
                    );

                    setValue(value.slice(0, closestIdx) + value.slice(closestIdx + 1));
                    event.preventDefault();

                    if (closestIdx && inputRef.current) {
                        setInputSelection(inputRef.current, closestIdx);
                    }

                    handleChange({
                        value: value.slice(0, closestIdx) + value.slice(closestIdx + 1),
                        event: event as ChangeEvent<HTMLInputElement>,
                        name: '',
                    });
                }
            } else {
                isDeleteOperation.current = false;
            }

            if (onKeyDown)
                onKeyDown({
                    event: event as KeyboardEvent<HTMLInputElement>,
                    name,
                    value: String(newValue),
                });
        },
        [handleChange, inputRef, maskOptions, onKeyDown, value]
    );

    const handleCancel = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            const formattedValue = getFormatValue('');

            setValue(formattedValue);

            if (onCancel) onCancel(event);
        },
        [getFormatValue, onCancel]
    );

    const handleMouseUp: TMouseEventHandler = useCallback(
        ({ event }) => {
            correctCarriagePosition(event, maskOptions.prefix.length);
        },
        [maskOptions]
    );

    const handleFocus: TFocusEventHandler = data => {
        onFocus?.(data);

        if (value === maskOptions.prefix) {
            correctCarriagePosition(data.event, maskOptions.prefix.length);
        }
    };

    return {
        value,
        isDeleteOperation,
        maskOptions,
        beforeInputSelection,
        handleChange,
        handleKeyDown,
        handleCancel,
        handleFocus,
        handleMouseUp,
    };
};
